import { CurrencyCode } from '@liveauctioneers/hammer-ui-liveauctioneers/lotDetail';
import {
    getCatalogCurrency,
    getCatalogIsLAPayments,
    getCatalogPaymentProvider,
} from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getCurrencyConversion } from '@/redux/modules/currency';
import { getHasValidCardOnFile } from '@/redux/modules/account/payments/creditCard/creditCard/creditCard.selectors';
import { getItemSummaryCatalogId } from '@/redux/modules/item/summary/itemSummary.selectors';
import { LiveBidConsoleCatalog } from '@liveauctioneers/hammer-ui-liveauctioneers/desktopLiveBidPanel';
import { preferredCurrencySelector } from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useMemo } from 'react';

type Props = {
    itemId: number;
    selectedCurrency?: string;
};

export const useGetBiddingConsoleCatalogData = ({ itemId, selectedCurrency }: Props): LiveBidConsoleCatalog => {
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const currency = useAppSelector((state) => getCatalogCurrency(state, catalogId));
    const livePaymentsEnabled = useAppSelector((state) => getCatalogIsLAPayments(state, catalogId));
    const paymentProvider = useAppSelector((state) => getCatalogPaymentProvider(state, catalogId));
    const hasCreditCardOnFile = useAppSelector((state) => getHasValidCardOnFile(state, paymentProvider));

    const preferredCurrency = useAppSelector(preferredCurrencySelector);

    // Use selectedCurrency if available, otherwise fall back to preferredCurrency
    const targetCurrency = selectedCurrency || preferredCurrency;
    const preferredCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, targetCurrency));
    const sourceCurrencyConversion = useAppSelector((state) => getCurrencyConversion(state, currency));

    return useMemo(() => {
        return {
            auctionStatus: 'live',
            catalogId,
            currency: {
                hideConversionBadge: false,
                preferredCurrencyConversion,
                sourceCurrencyCode: currency.toUpperCase() as CurrencyCode,
                sourceCurrencyConversion,
            },
            livePayments: {
                hasCreditCardOnFile,
                livePaymentsEnabled,
            },
        };
    }, [
        catalogId,
        currency,
        hasCreditCardOnFile,
        livePaymentsEnabled,
        preferredCurrencyConversion,
        sourceCurrencyConversion,
    ]);
};
